import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import { Exam } from './examData';
import { supabase } from './supabaseClient';

const MathText: React.FC<{ math: string }> = ({ math }) => {
    if (math.includes('\\begin{')) {
        return <InlineMath math={math} />;
    }

    const parts: { type: 'text' | 'math'; content: string }[] = [];
    let remaining = math;

    while (remaining.length > 0) {
        const textStart = remaining.indexOf('\\text{');
        if (textStart === -1) {
            if (remaining.trim()) parts.push({ type: 'math', content: remaining });
            break;
        }
        if (textStart > 0) {
            parts.push({ type: 'math', content: remaining.slice(0, textStart) });
        }
        let braceCount = 0;
        let i = textStart + 6;
        for (; i < remaining.length; i++) {
            if (remaining[i] === '{') braceCount++;
            if (remaining[i] === '}') {
                if (braceCount === 0) break;
                braceCount--;
            }
        }
        const textContent = remaining.slice(textStart + 6, i);
        parts.push({ type: 'text', content: textContent });
        remaining = remaining.slice(i + 1);
    }

    return (
        <span style={{ display: 'inline' }}>
            {parts.map((part, idx) =>
                part.type === 'text' ? (
                    <span key={idx}>{part.content}</span>
                ) : (
                    <InlineMath key={idx} math={part.content} />
                )
            )}
        </span>
    );
};

const ExplanationContent: React.FC<{ text: string }> = ({ text }) => {
    const lines = text.split('\n');
    return (
        <>
            {lines.map((line, i) => {
                const hasLatex = line.includes('\\');
                return (
                    <div key={i} style={{ marginBottom: i < lines.length - 1 ? '6px' : 0 }}>
                        {hasLatex ? <MathText math={line} /> : line}
                    </div>
                );
            })}
        </>
    );
};

/* ── Layout wrappers ── */

const PageWrapper = styled.div`
    display: flex;
    min-height: calc(100vh - 56px);
    background-color: #f8fafc;
`;

const ExamPane = styled.div`
    flex: 1;
    max-width: 900px;
    margin: 0 auto;
    padding: 40px 20px;
    min-width: 0;

    @media (max-width: 600px) {
        padding: 16px 12px;
    }
`;

/* ── Exam components ── */

const Header = styled.div`
    text-align: center;
    margin-bottom: 32px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    color: #00205B;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    padding: 8px 0;
    margin-bottom: 16px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'Roboto', sans-serif;

    &:hover {
        text-decoration: underline;
    }
`;

const ExamTitle = styled.h1`
    color: #00205B;
    font-size: 2rem;
    font-weight: 700;
    margin: 0 0 8px 0;

    @media (max-width: 600px) {
        font-size: 1.3rem;
    }
`;

const ExamSubtitle = styled.p`
    color: #64748b;
    font-size: 1rem;
    margin: 0;
`;

const ProgressBarStyled = styled.div`
    background: #ffffff;
    border-radius: 12px;
    padding: 16px 24px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    border: 1px solid #e2e8f0;
`;

const ProgressText = styled.span`
    font-weight: 600;
    color: #334155;
    font-size: 0.95rem;
`;

const QuestionNav = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 24px;
    justify-content: center;
`;

interface NavDotProps {
    status: 'current' | 'correct' | 'incorrect' | 'unanswered';
}

const NavDot = styled.button<NavDotProps>`
    width: 36px;
    height: 36px;
    border-radius: 8px;
    font-size: 0.8rem;

    @media (max-width: 600px) {
        width: 28px;
        height: 28px;
        font-size: 0.7rem;
    }
    font-weight: 700;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 2px solid ${props => {
        if (props.status === 'current') return '#00205B';
        if (props.status === 'correct') return '#22c55e';
        if (props.status === 'incorrect') return '#ef4444';
        return '#cbd5e1';
    }};
    background: ${props => {
        if (props.status === 'current') return '#00205B';
        if (props.status === 'correct') return '#dcfce7';
        if (props.status === 'incorrect') return '#fee2e2';
        return '#ffffff';
    }};
    color: ${props => {
        if (props.status === 'current') return '#ffffff';
        if (props.status === 'correct') return '#166534';
        if (props.status === 'incorrect') return '#991b1b';
        return '#64748b';
    }};

    &:hover {
        transform: scale(1.1);
    }
`;

const QuestionCard = styled.div`
    background: #ffffff;
    border-radius: 14px;
    padding: 32px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    border: 1px solid #e2e8f0;

    @media (max-width: 600px) {
        padding: 16px;
        border-radius: 10px;
    }
`;

const QuestionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const QuestionNumber = styled.span`
    font-weight: 700;
    color: #00205B;
    font-size: 1.2rem;
`;

const PointsBadge = styled.span`
    font-size: 0.85rem;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 6px;
`;

const SourceBadge = styled.span`
    font-size: 0.8rem;
    font-weight: 600;
    color: #6366f1;
    background: #eef2ff;
    padding: 4px 10px;
    border-radius: 6px;
    border: 1px solid #c7d2fe;
`;

const QuestionText = styled.div`
    margin-bottom: 24px;
    font-size: 1.1rem;
    line-height: 2;
    color: #1e293b;
    font-family: 'Times New Roman', Times, serif;
    overflow-x: auto;

    @media (max-width: 600px) {
        font-size: 0.95rem;
        line-height: 1.8;
    }
`;

const QuestionImage = styled.img`
    max-width: 100%;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    margin-bottom: 20px;
`;

const FreeResponseLabel = styled.div`
    background: #fefce8;
    border: 1px solid #fde68a;
    border-radius: 10px;
    padding: 16px 20px;
    color: #92400e;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: 'Roboto', sans-serif;
    text-align: center;
`;

interface ExplanationTextProps {
    variant: 'correct' | 'incorrect' | 'neutral';
}

const ExplanationText = styled.div<ExplanationTextProps>`
    margin-top: -4px;
    margin-bottom: 14px;
    padding: 14px 18px;
    font-size: 0.95rem;
    font-family: 'Roboto', sans-serif;
    line-height: 1.7;
    letter-spacing: 0.01em;
    border-radius: 0 0 8px 8px;
    white-space: pre-line;
    cursor: pointer;
    color: ${props => {
        if (props.variant === 'correct') return '#14532d';
        if (props.variant === 'incorrect') return '#7f1d1d';
        return '#334155';
    }};
    background: ${props => {
        if (props.variant === 'correct') return '#f0fdf4';
        if (props.variant === 'incorrect') return '#fef2f2';
        return '#f8fafc';
    }};
    border: 1px solid ${props => {
        if (props.variant === 'correct') return '#bbf7d0';
        if (props.variant === 'incorrect') return '#fecaca';
        return '#e2e8f0';
    }};
    border-top: none;
`;

const ExplanationToggle = styled.button<ExplanationTextProps>`
    display: block;
    width: 100%;
    margin-top: -4px;
    margin-bottom: 12px;
    padding: 8px 18px;
    font-size: 0.85rem;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    border-radius: 0 0 8px 8px;
    text-align: left;
    color: ${props => {
        if (props.variant === 'correct') return '#15803d';
        if (props.variant === 'incorrect') return '#b91c1c';
        return '#64748b';
    }};
    background: ${props => {
        if (props.variant === 'correct') return '#f0fdf4';
        if (props.variant === 'incorrect') return '#fef2f2';
        return '#f8fafc';
    }};
    border: 1px solid ${props => {
        if (props.variant === 'correct') return '#bbf7d0';
        if (props.variant === 'incorrect') return '#fecaca';
        return '#e2e8f0';
    }};
    border-top: none;
    &:hover {
        opacity: 0.8;
    }
`;

interface ChoiceButtonProps {
    state: 'default' | 'correct' | 'incorrect';
}

const ChoiceButton = styled.button<ChoiceButtonProps>`
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    padding: 16px 20px;
    margin-bottom: 12px;
    overflow-x: auto;

    @media (max-width: 600px) {
        padding: 12px 14px;
    }
    border-radius: 10px;
    font-size: 1rem;
    font-family: 'Times New Roman', Times, serif;
    cursor: ${props => props.state === 'default' ? 'pointer' : 'default'};
    transition: all 0.2s ease;
    background: ${props => {
        if (props.state === 'correct') return '#dcfce7';
        if (props.state === 'incorrect') return '#fee2e2';
        return '#ffffff';
    }};
    border: 2px solid ${props => {
        if (props.state === 'correct') return '#22c55e';
        if (props.state === 'incorrect') return '#ef4444';
        return '#e2e8f0';
    }};
    color: ${props => {
        if (props.state === 'correct') return '#166534';
        if (props.state === 'incorrect') return '#991b1b';
        return '#334155';
    }};

    &:hover {
        ${props => props.state === 'default' && `
            border-color: #00205B;
            background: #f8fafc;
        `}
    }
`;

const ChoiceLabel = styled.span`
    font-weight: 700;
    margin-right: 10px;
    opacity: 0.6;
`;

const NavButtons = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
`;

const NavButton = styled.button`
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 0.95rem;

    @media (max-width: 600px) {
        padding: 10px 16px;
        font-size: 0.85rem;
    }
    font-weight: 600;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #00205B;
    color: #ffffff;
    border: none;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        background: #cbd5e1;
        cursor: not-allowed;
    }
`;

const SkipButton = styled.button`
    padding: 12px 28px;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    background: transparent;
    color: #64748b;
    border: 2px solid #e2e8f0;

    &:hover {
        border-color: #00205B;
        color: #00205B;
    }
`;

/* ── Component ── */

const TimerBar = styled.div`
    background: #ffffff;
    border-radius: 12px;
    padding: 12px 24px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    border: 1px solid #e2e8f0;
`;

const TimerDisplay = styled.span<{ urgent: boolean }>`
    font-size: 1.1rem;
    font-weight: 700;
    font-family: 'Roboto Mono', monospace;
    color: ${props => props.urgent ? '#ef4444' : '#00205B'};
`;

const SubmitExamButton = styled.button`
    padding: 12px 32px;
    border-radius: 10px;
    font-size: 1rem;
    font-weight: 700;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    color: #ffffff;
    border: none;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        background: #cbd5e1;
        cursor: not-allowed;
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalBox = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 32px;
    max-width: 420px;
    width: 90%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    text-align: center;
    font-family: 'Roboto', sans-serif;
`;

const ModalTitle = styled.h3`
    margin: 0 0 12px 0;
    font-size: 1.25rem;
    color: #1e293b;
`;

const ModalText = styled.p`
    margin: 0 0 24px 0;
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.5;
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: center;
`;

const ModalCancelButton = styled.button`
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    background: #f1f5f9;
    color: #334155;
    border: 1px solid #e2e8f0;
    &:hover { background: #e2e8f0; }
`;

const ModalSubmitButton = styled.button`
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    background: #ef4444;
    color: #ffffff;
    border: none;
    &:hover { background: #dc2626; }
`;

const SelectedIndicator = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #00205B;
    margin-left: auto;
    flex-shrink: 0;
`;

const RestartButton = styled.button`
    padding: 10px 24px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: 'Roboto', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    background: transparent;
    color: #ef4444;
    border: 2px solid #ef4444;

    &:hover {
        background: #ef4444;
        color: #ffffff;
    }
`;

interface ExamViewProps {
    exam: Exam;
    onBack: () => void;
    userId?: string;
    timed?: boolean;
}

// Seeded shuffle: produces a stable permutation for a given question key
const seededShuffle = (length: number, seed: string): number[] => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }
    const indices = Array.from({ length }, (_, i) => i);
    for (let i = length - 1; i > 0; i--) {
        hash = ((hash << 5) - hash + i) | 0;
        const j = ((hash >>> 0) % (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
};

const ExamView: React.FC<ExamViewProps> = ({ exam, onBack, userId, timed = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, number>>({});
    const [expandedExplanations, setExpandedExplanations] = useState<Record<string, boolean>>({});
    const [submitted, setSubmitted] = useState(false);
    const [timeLeft, setTimeLeft] = useState(timed ? 3600 : 0); // 1 hour in seconds
    const isCustom = exam.id.startsWith('custom-');

    // Timer countdown
    useEffect(() => {
        if (!timed || submitted || timeLeft <= 0) return;
        const interval = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    setSubmitted(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [timed, submitted, timeLeft]);

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const [showConfirm, setShowConfirm] = useState(false);

    const handleSubmitClick = () => {
        const mcqCount = exam.questions.filter(q => !q.freeResponse).length;
        const answeredCount = Object.keys(answers).length;
        if (answeredCount < mcqCount) {
            setShowConfirm(true);
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        setShowConfirm(false);
        setSubmitted(true);
        if (userId) {
            const rows = exam.questions
                .map((q, idx) => ({ q, idx }))
                .filter(({ q }) => !q.freeResponse)
                .filter(({ q, idx }) => {
                    const k = `${idx}-${q.id}${q.sub || ''}`;
                    return answers[k] !== undefined;
                })
                .map(({ q, idx }) => {
                    const k = `${idx}-${q.id}${q.sub || ''}`;
                    return {
                        user_id: userId,
                        exam_id: exam.id,
                        question_key: k,
                        selected_index: answers[k],
                        correct_index: q.correctIndex,
                    };
                });
            if (rows.length > 0) {
                await supabase.from('user_answers').upsert(rows, {
                    onConflict: 'user_id,exam_id,question_key',
                });
            }
            if (isCustom) {
                const stored = JSON.parse(localStorage.getItem('customExams') || '{}');
                stored[exam.id] = {
                    id: exam.id,
                    courseId: exam.courseId,
                    title: exam.title,
                    questionCount: exam.questions.filter(q => !q.freeResponse).length,
                };
                localStorage.setItem('customExams', JSON.stringify(stored));
            }
        }
    };

    // Build shuffled choice orders for all questions (stable across renders)
    const shuffleMap = React.useMemo(() => {
        const map: Record<string, number[]> = {};
        exam.questions.forEach((qu, idx) => {
            const k = `${idx}-${qu.id}${qu.sub || ''}`;
            if (!qu.freeResponse && qu.choices.length > 0) {
                map[k] = seededShuffle(qu.choices.length, `${exam.id}-${k}`);
            }
        });
        return map;
    }, [exam]);

    // Load saved answers from Supabase on mount (not in timed mode)
    const loadSavedAnswers = useCallback(async () => {
        if (!userId || timed) return;
        const { data } = await supabase
            .from('user_answers')
            .select('question_key, selected_index')
            .eq('user_id', userId)
            .eq('exam_id', exam.id);
        if (data && data.length > 0) {
            const saved: Record<string, number> = {};
            data.forEach(row => { saved[row.question_key] = row.selected_index; });
            setAnswers(saved);
        }
    }, [userId, exam.id, timed]);

    useEffect(() => {
        loadSavedAnswers();
    }, [loadSavedAnswers]);


    const getQuestionKey = (idx: number, qId: number, sub?: string) => `${idx}-${qId}${sub || ''}`;

    const q = exam.questions[currentIndex];
    const key = getQuestionKey(currentIndex, q.id, q.sub);
    const answered = answers[key] !== undefined;
    const userAnswer = answers[key];

    // Get the shuffle order for the current question
    const order = shuffleMap[key] || [];

    const handleAnswer = async (displayIndex: number) => {
        if (timed && !submitted) {
            // In timed mode before submit: allow changing answers
            const originalIndex = order[displayIndex];
            setAnswers(prev => ({ ...prev, [key]: originalIndex }));
            return;
        }
        if (answered) return;
        const originalIndex = order[displayIndex];
        setAnswers(prev => ({ ...prev, [key]: originalIndex }));

        if (userId) {
            const { error } = await supabase.from('user_answers').upsert({
                user_id: userId,
                exam_id: exam.id,
                question_key: key,
                selected_index: originalIndex,
                correct_index: q.correctIndex,
            }, { onConflict: 'user_id,exam_id,question_key' });
            if (error) console.error('Failed to save answer:', error);
        }
    };

    const totalAnswered = Object.keys(answers).length;
    const totalCorrect = exam.questions.filter((qu, idx) => {
        const k = getQuestionKey(idx, qu.id, qu.sub);
        return answers[k] === qu.correctIndex;
    }).length;

    const handleRestart = async () => {
        if (userId && !isCustom) {
            await supabase
                .from('user_answers')
                .delete()
                .eq('user_id', userId)
                .eq('exam_id', exam.id);
        }
        setAnswers({});
        setCurrentIndex(0);
    };

    const goNext = () => {
        if (currentIndex < exam.questions.length - 1) setCurrentIndex(currentIndex + 1);
    };
    const goPrev = () => {
        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
    };

    // In timed mode before submit: show selected vs unanswered (no correct/incorrect)
    const showResults = !timed || submitted;

    const getNavStatus = (idx: number): 'current' | 'correct' | 'incorrect' | 'unanswered' => {
        if (idx === currentIndex) return 'current';
        const qu = exam.questions[idx];
        const k = getQuestionKey(idx, qu.id, qu.sub);
        if (answers[k] === undefined) return 'unanswered';
        if (!showResults) return 'current'; // just show as "answered" before submit
        return answers[k] === qu.correctIndex ? 'correct' : 'incorrect';
    };


    const choiceLetters = ['A', 'B', 'C', 'D'];

    return (
        <PageWrapper>
            <ExamPane>
                <Header>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <BackButton onClick={onBack}>&larr; Back to exams</BackButton>
                        {!timed && <RestartButton onClick={handleRestart}>Restart</RestartButton>}
                    </div>
                    <ExamTitle>{exam.title}</ExamTitle>
                    <ExamSubtitle>{exam.courseId} &middot; {exam.questions.length} questions &middot; {exam.questions.reduce((s, qu) => s + qu.points, 0)} points</ExamSubtitle>
                </Header>

                {timed && !submitted && (
                    <TimerBar>
                        <TimerDisplay urgent={timeLeft < 300}>
                            Time Remaining: {formatTime(timeLeft)}
                        </TimerDisplay>
                        <SubmitExamButton onClick={handleSubmitClick}>
                            Submit Exam
                        </SubmitExamButton>
                    </TimerBar>
                )}

                <ProgressBarStyled>
                    <ProgressText>Answered: {totalAnswered} / {exam.questions.length}</ProgressText>
                    {showResults && (
                        <ProgressText>Correct: {totalCorrect} / {totalAnswered || 1}</ProgressText>
                    )}
                </ProgressBarStyled>

                <QuestionNav>
                    {exam.questions.map((_qu, idx) => (
                        <NavDot
                            key={idx}
                            status={getNavStatus(idx)}
                            onClick={() => setCurrentIndex(idx)}
                        >
                            {idx + 1}
                        </NavDot>
                    ))}
                </QuestionNav>

                <QuestionCard>
                    <QuestionHeader>
                        <QuestionNumber>
                            Question {q.id}{q.sub ? ` (${q.sub})` : ''}
                        </QuestionNumber>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {isCustom && q.sourceExam && (
                                <SourceBadge>{q.sourceExam}</SourceBadge>
                            )}
                            <PointsBadge>{q.points} pts</PointsBadge>
                        </div>
                    </QuestionHeader>
                    <QuestionText>
                        <MathText math={q.text} />
                    </QuestionText>
                    {q.image && (
                        <QuestionImage src={q.image} alt={`Question ${q.id} reference`} />
                    )}

                    <NavButtons>
                        <NavButton onClick={goPrev} disabled={currentIndex === 0}>
                            &larr; Previous
                        </NavButton>
                        {!answered && (
                            <SkipButton onClick={goNext}>
                                Skip &rarr;
                            </SkipButton>
                        )}
                        <NavButton onClick={goNext} disabled={currentIndex === exam.questions.length - 1}>
                            Next &rarr;
                        </NavButton>
                    </NavButtons>

                    {q.freeResponse ? (
                        <FreeResponseLabel>
                            Free response — work this out on paper
                        </FreeResponseLabel>
                    ) : (
                        order.map((originalIdx, displayIdx) => {
                            let state: 'default' | 'correct' | 'incorrect' = 'default';
                            const isSelected = userAnswer === originalIdx;
                            if (showResults && answered) {
                                if (originalIdx === q.correctIndex) state = 'correct';
                                else if (originalIdx === userAnswer) state = 'incorrect';
                            }
                            return (
                                <div key={displayIdx}>
                                    <ChoiceButton
                                        state={state}
                                        onClick={() => handleAnswer(displayIdx)}
                                        style={!showResults && isSelected ? {
                                            borderColor: '#00205B',
                                            background: '#f0f4ff',
                                        } : undefined}
                                    >
                                        <ChoiceLabel>{choiceLetters[displayIdx]}.</ChoiceLabel>
                                        <MathText math={q.choices[originalIdx]} />
                                        {!showResults && isSelected && <SelectedIndicator />}
                                    </ChoiceButton>
                                    {showResults && answered && q.explanations && q.explanations[originalIdx] && (() => {
                                        const explKey = `${currentIndex}-${originalIdx}`;
                                        const variant = originalIdx === q.correctIndex ? 'correct' as const : originalIdx === userAnswer ? 'incorrect' as const : 'neutral' as const;
                                        const isExpanded = expandedExplanations[explKey];
                                        return isExpanded ? (
                                            <ExplanationText variant={variant} onClick={() => setExpandedExplanations(prev => ({ ...prev, [explKey]: false }))}>
                                                <ExplanationContent text={q.explanations[originalIdx]} />
                                            </ExplanationText>
                                        ) : (
                                            <ExplanationToggle variant={variant} onClick={() => setExpandedExplanations(prev => ({ ...prev, [explKey]: true }))}>
                                                Click to see explanation
                                            </ExplanationToggle>
                                        );
                                    })()}
                                </div>
                            );
                        })
                    )}
                </QuestionCard>
            </ExamPane>

            {showConfirm && (
                <ModalOverlay onClick={() => setShowConfirm(false)}>
                    <ModalBox onClick={e => e.stopPropagation()}>
                        <ModalTitle>Submit exam?</ModalTitle>
                        <ModalText>
                            You have answered {Object.keys(answers).length} of {exam.questions.filter(q => !q.freeResponse).length} questions.
                            Unanswered questions will be marked incorrect.
                        </ModalText>
                        <ModalButtons>
                            <ModalCancelButton onClick={() => setShowConfirm(false)}>
                                Go Back
                            </ModalCancelButton>
                            <ModalSubmitButton onClick={handleSubmit}>
                                Submit Anyway
                            </ModalSubmitButton>
                        </ModalButtons>
                    </ModalBox>
                </ModalOverlay>
            )}
        </PageWrapper>
    );
};

export default ExamView;
